/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import {
    Card,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Table,
} from "@jhool-io/fe-components";
import ListState from "../../../../../components/ListState/ListState";
import Loader from "../../../../../shared-ui/Loader/Loader";
import { useFetchClientSmsHistory } from "../../../hooks/clients.queries";
import { useDoesUserHavePermission } from "../../../../../hooks/permissions";
import { UserPermisions } from "../../../../../utils/types/user";
import {
    CommunicationDirection,
    CommunicationNoteChannel,
    IClientSmsMessage,
    SmsReadStatus,
} from "../../../types/clients.types";
import { formatDate } from "../../../../../utils/helpers";
import {
    COMMUNICATION_HISTORY_DATE_FORMAT,
    LISTS_DEFAULT_LIMIT,
} from "../../../../../utils/constants";
import CommunicationPhoneLink from "../CommunicationPhoneLink/CommunicationPhoneLink";
import { getRingCentralUrl } from "../../../helpers/clients.helpers";
import CommunicationDirectionIcon from "../CommunicationDirectionIcon/CommunicationDirectionIcon";
import CommunicationNotes from "../CommunicationNotes/CommunicationNotes";
import CommunicationNotesCell from "../CommunicationNotesCell/CommunicationNotesCell";

const RINGCENTRAL_MESSAGE_ID = /^\d+$/;

export default function SmsHistoryList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const clientId = params.clientId as string;

    const pageFilter = Number(searchParams.get("sms_page")) || 1;
    const limitFilter =
        Number(searchParams.get("sms_limit")) || LISTS_DEFAULT_LIMIT;
    const directionFilter = searchParams.get("sms_direction") ?? "";
    const readStatusFilter = searchParams.get("sms_read_status") ?? "";
    const notesRecordId = searchParams.get("sms_notes_record_id");

    const canAddNote = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
        UserPermisions.CLIENT_INFO_COMMUNICATIONS_WRITE,
    ]);

    const { data, isLoading, error, isSuccess } = useFetchClientSmsHistory(
        clientId,
        {
            page: pageFilter,
            limit: limitFilter,
            direction: directionFilter,
            read_status: readStatusFilter,
        }
    );

    const recordWithNotesInView = data?.data.find(
        (record) => record.message_id === notesRecordId
    );

    const handleFilterChange = (paramName: string, value: string) => {
        if (value === "All") searchParams.delete(paramName);
        else searchParams.set(paramName, value);
        searchParams.delete("sms_page");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("sms_page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("sms_limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleCloseNotes = () => {
        searchParams.delete("sms_notes_record_id");
        setSearchParams(searchParams);
    };

    const getClientNumber = (row: Row<IClientSmsMessage>) => {
        if (row.original.direction === CommunicationDirection.INBOUND) {
            return row.original.from;
        }
        if (row.original.direction === CommunicationDirection.OUTBOUND) {
            return row.original.to;
        }
        return null;
    };

    const handleRowClick = (row: Row<IClientSmsMessage>) => {
        const clientNumber = getClientNumber(row);
        if (!clientNumber) return;

        const numberHistoryUrl = getRingCentralUrl(clientNumber, "sms-history");
        if (numberHistoryUrl) {
            window.open(numberHistoryUrl, "_blank", "noopener,noreferrer");
        }
    };

    const columns: ColumnDef<IClientSmsMessage>[] = React.useMemo(
        () => [
            {
                accessorKey: "sent_at",
                header: "Date",
                cell: ({ row }) => (
                    <div className="flex items-center gap-x-2">
                        <CommunicationDirectionIcon
                            direction={row.original.direction}
                        />
                        <span>
                            {row.original.sent_at
                                ? formatDate(
                                      row.original.sent_at,
                                      false,
                                      COMMUNICATION_HISTORY_DATE_FORMAT
                                  )
                                : "--"}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "from",
                header: "Sender",
                cell: ({ row }) => (
                    <CommunicationPhoneLink
                        value={row.original.from}
                        destination="new-sms"
                    />
                ),
            },
            {
                accessorKey: "to",
                header: "Recipient",
                cell: ({ row }) => (
                    <CommunicationPhoneLink
                        value={row.original.to}
                        destination="new-sms"
                    />
                ),
            },
            {
                accessorKey: "message",
                header: "Message",
                cell: ({ row }) => (
                    <span className="whitespace-pre-wrap">
                        {row.original.message || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "delivery_status",
                header: "Delivery status",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {row.original.delivery_status || "--"}
                    </span>
                ),
            },
            {
                accessorKey: "notes",
                header: "Notes",
                cell: ({ row }) => (
                    <CommunicationNotesCell
                        notesCount={row.original.notes?.length || 0}
                        canAddNote={
                            canAddNote &&
                            RINGCENTRAL_MESSAGE_ID.test(row.original.message_id)
                        }
                        onClick={() => {
                            searchParams.set(
                                "sms_notes_record_id",
                                row.original.message_id
                            );
                            setSearchParams(searchParams);
                        }}
                    />
                ),
            },
        ],
        [canAddNote, searchParams, setSearchParams]
    );

    return (
        <div>
            {recordWithNotesInView ? (
                <CommunicationNotes
                    channel={CommunicationNoteChannel.SMS}
                    recordId={recordWithNotesInView.message_id}
                    notes={recordWithNotesInView.notes || []}
                    canAddNote={
                        canAddNote &&
                        RINGCENTRAL_MESSAGE_ID.test(
                            recordWithNotesInView.message_id
                        )
                    }
                    onClose={handleCloseNotes}
                />
            ) : null}
            <Card classNames="mb-[--spacing-header]">
                <div className="flex items-center gap-[--spacing-items] flex-wrap">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {directionFilter || "Direction"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="150px">
                            {[
                                "All",
                                ...Object.values(CommunicationDirection),
                            ].map((direction) => (
                                <DropdownItem
                                    key={direction}
                                    onClick={() =>
                                        handleFilterChange(
                                            "sms_direction",
                                            direction
                                        )
                                    }
                                >
                                    {direction}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                {readStatusFilter || "Read status"}
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="150px">
                            {["All", ...Object.values(SmsReadStatus)].map(
                                (readStatus) => (
                                    <DropdownItem
                                        key={readStatus}
                                        onClick={() =>
                                            handleFilterChange(
                                                "sms_read_status",
                                                readStatus
                                            )
                                        }
                                    >
                                        {readStatus}
                                    </DropdownItem>
                                )
                            )}
                        </DropdownContent>
                    </Dropdown>
                </div>
            </Card>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ??
                        "Cannot display SMS history at this time"
                    }
                />
            )}
            {isSuccess && data.data.length === 0 && (
                <ListState
                    isError={false}
                    stateHelperText="Messages will appear here once communication begins"
                    emptyMessage="No SMS messages to display"
                />
            )}
            {isSuccess && data.data.length > 0 && (
                <>
                    <Table
                        columns={columns}
                        data={data.data}
                        handleRowClick={handleRowClick}
                    />
                    <Pagination
                        totalCount={data.total_count}
                        totalPages={data.total_pages}
                        currentPage={pageFilter}
                        onPageChange={handlePageChange}
                        limit={limitFilter}
                        onLimitChange={handleLimitChange}
                    />
                </>
            )}
        </div>
    );
}
